import {BehaviorSubject, Subject, Subscription, take} from 'rxjs';
import {inject, Injectable, OnDestroy} from '@angular/core';
import {Core} from 'cytoscape';
import {MapService} from '../api/services/map.service';
import {GraphNode, GraphNodeLink} from '../api/interfaces/graph-node.interface';
import {NodeService} from '../api/services/node.service';
import {assign, first, omit, size, uniqBy} from 'lodash';
import {DashboardComponent} from '../dashboard/dashboard.component';
import {CytoscapeService} from './cy-services/cytoscape.service';
import {getTopLevelParents} from './undo-redo.service';
import {UserService} from '../api/services/user.service';
import {MetaData} from '../api/interfaces/metadata';
import {updateInProgress} from '../shared/helpers/signal.helpers';
import {Permission} from '../acl/map-user-roles.enum';
import {MapRoleService} from '../acl/map-role.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../dialogs/confirmation-dialog.component';
import {
  CommandFactoryService,
  DeleteOverlayElementCommand,
  OverlayElementSnapshot,
  UndoRedoManagerService
} from './undo-redo';
import {OverlayElementManagerService} from './overlay-element-manager.service';
import {OverlayElementApiService} from '../api/services/overlay-element.service';
import {SidebarService} from '../sidebar-form/sidebar.service';
import {TranslateService} from '@ngx-translate/core';
import {canvasOwnsSelectAll, isTextFieldActive} from './select-all-scope';
import {CommentsStateManager} from '../shared/components/comments/comments.state-manager';

@Injectable({
  providedIn: 'root'
})
export class ButtonPressService implements OnDestroy {
  public static isShiftPressed = false;
  public static isAltPressed = false;
  private ctrlKeyPressed: boolean;
  private copyStream: Subject<void>;
  // @ts-ignore
  private nodeCreationStream: BehaviorSubject<GraphNode[]> = new BehaviorSubject<GraphNode[]>(null);
  private pasteStream = new Subject<KeyboardEvent>();
  private deleteStream: Subject<void> = new Subject<void>();
  private selectAllStream: Subject<void> = new Subject<void>();
  private cutStream: Subject<void> = new Subject<void>();

  private mousePosition = { x: 0, y: 0 };
  private commentsStateManager = inject(CommentsStateManager);
  private contextPressPosition: { x: number; y: number } | null = null;
  private subs = new Subscription();

  constructor(private mapService: MapService,
              private userService: UserService,
              private cyService: CytoscapeService,
              private mapRoleService: MapRoleService,
              private undoRedoManager: UndoRedoManagerService,
              private commandFactory: CommandFactoryService,
              private nodeService: NodeService,
              private matDialog: MatDialog,
              private overlayManager: OverlayElementManagerService,
              private overlayElementService: OverlayElementApiService,
              private sidebarService: SidebarService,
              private translate: TranslateService) {
    this.ctrlKeyPressed = false;
    this.copyStream = new Subject<void>();
    this.addAllSubs();
  }

  public init(cy: Core) {

    localStorage.removeItem('clipboard');
    document.addEventListener('keydown', this.handleKeyDownEvent);
    document.addEventListener('keyup', this.handleKeyUpEvent);
    setTimeout(() => {
      if (this.cyService.cy) {
        this.cyService.cy.off('mousemove', this.trackMousePosition);
        this.cyService.cy.on('mousemove', this.trackMousePosition);
        this.cyService.cy.off('cxttapstart', this.trackContextPressPosition);
        this.cyService.cy.on('cxttapstart', this.trackContextPressPosition);
      }
    }, 500);
    this.addAllSubs();
    this.handleKeyEvents();
    return this.nodeCreationStream.asObservable();
  }

  public getNodeCreation() {
    return this.nodeCreationStream.asObservable();
  }

  public initUndoRedo() {
    const undoRedoManager = this.undoRedoManager;

    document.addEventListener('keydown', async (event) => {
      // Checking if the Command key is pressed on macOS or Control key on Windows/Linux
      const modifierKey = event.ctrlKey || (event.metaKey && navigator.platform.toUpperCase().indexOf('MAC') >= 0) || event.metaKey;

      if (modifierKey && ['z', 'Z', 'y', 'Y'].includes(event.key) && this.holdForCommentDraft(event)) return;

      if (modifierKey) {
        // Undo: Cmd/Ctrl + Z (without Shift)
        if ((event.key === 'z' || event.key === 'Z') && !event.shiftKey) {
          event.preventDefault();
          await undoRedoManager.undo();
        }

        // Redo for Windows/Linux: Ctrl + Y
        if (event.ctrlKey && (event.key === 'y' || event.key === 'Y')) {
          event.preventDefault();
          await undoRedoManager.redo();
        }

        // Redo for macOS: Cmd + Shift + Z
        if (event.metaKey && event.shiftKey && (event.key === 'z' || event.key === 'Z')) {
          event.preventDefault();
          await undoRedoManager.redo();
        }
      }
    });
  }

  public copyFromClipboard(parent?: GraphNode | null, atPos?: { x: number; y: number }) {
    if (!parent) {
      parent = DashboardComponent.tappedNode;
    }
    if (document.activeElement !== document.body) return;
    const clipboard = localStorage.getItem('clipboard');
    if (clipboard === null) {
      return;
    } else {
      const mousePosition = atPos ?? this.getPosition();
      const elements = JSON.parse(clipboard as string);
      const nodes = elements.filter((element: any) => element.group === 'nodes');
      const nPos = nodes.map((e: any) => e.position);
      const target_X = mousePosition.x;
      const target_Y = mousePosition.y;

      const centerOfMass_X = nPos.reduce((sum: any, pos: { x: number, y: number }) => sum + pos.x, 0) / nPos.length;
      const centerOfMass_Y = nPos.reduce((sum: any, pos: { x: number, y: number }) => sum + pos.y, 0) / nPos.length;

      const relativePositions = nPos.map((pos: { x: number, y: number }) => ({
        x: pos.x - centerOfMass_X,
        y: pos.y - centerOfMass_Y
      }));

      const newPos: { x: number, y: number }[] = relativePositions.map((relativePos: { x: number, y: number }) => ({
        x: target_X + relativePos.x,
        y: target_Y + relativePos.y
      }));

      // Calculate the offset to apply to link handles
      const offsetX = target_X - centerOfMass_X;
      const offsetY = target_Y - centerOfMass_Y;

      const newNodes = nodes.map((e: any, index: number) => {
        const nodeData = {...e.data, position: newPos[index]};

        // Offset link handle positions (bendPointPositions and controlPointPositions)
        if (nodeData.links && nodeData.links.length > 0) {
          nodeData.links = nodeData.links.map((link: any) => {
            const updatedLink = {...link};

            if (updatedLink.bendPointPositions && Array.isArray(updatedLink.bendPointPositions)) {
              updatedLink.bendPointPositions = updatedLink.bendPointPositions.map((pos: { x: number, y: number }) => ({
                x: pos.x + offsetX,
                y: pos.y + offsetY
              }));
            }

            if (updatedLink.controlPointPositions && Array.isArray(updatedLink.controlPointPositions)) {
              updatedLink.controlPointPositions = updatedLink.controlPointPositions.map((pos: {
                x: number,
                y: number
              }) => ({
                x: pos.x + offsetX,
                y: pos.y + offsetY
              }));
            }

            return updatedLink;
          });
        }

        return nodeData;
      }) as GraphNode[];
      const currentMap = this.mapService.getCurrentSelectedMapFromStore();
      // omit id for all metadata
      newNodes.forEach((n) => {
        if (n.metadata) {
          n.metadata = n.metadata.map((m) => omit(m, ['id']) as MetaData);
        }
      });

      // Workaround: Find loose children and add them to their parent's children array
      newNodes.forEach(node => {
        if (node.parent) {
          const parentId = typeof node.parent === 'object' ? node.parent.id : node.parent;
          const parentNode = newNodes.find(n => n.id === parentId);
          if (parentNode && parentNode.children && !parentNode.children.some(child => child.id === node.id)) {
            parentNode.children.push(node);
          }
        }
      });

      this.mapService.copyToMap(newNodes, currentMap.id, parent).subscribe((res) => {
        this.nodeCreationStream.next(res);
        // Find a top-level node to focus (one that's not contained in another pasted node)
        const pastedNodeIds = new Set(res.map((n) => n.id));
        const topLevelNodes = res.filter((node) => {
          // A node is top-level if it has no parent, or its parent is not among the pasted nodes
          if (!node.parent) return true;
          const parentId = typeof node.parent === 'object' ? node.parent.id : node.parent;
          return !pastedNodeIds.has(parentId);
        });
        const toFocus = (first(topLevelNodes) || first(res)) as GraphNode;
        const cy = this.cyService.cy;
        if (!cy) return;
        const cyElementsIds = res.map((e) => e.id);
        cy.elements(':selected').removeClass('highlighted');
        cy.elements(':selected').unselect();
        DashboardComponent.tappedNode = null;
        DashboardComponent.tappedLink = null;
        DashboardComponent.multiSelectedElements = [];

        // Move newly pasted nodes to their parent compound nodes
        if (parent && parent.id) {
          res.forEach((node: GraphNode) => {
            // @ts-ignore
            if (node.parent && node.parent.id === parent.id) {
              setTimeout(() => {
                const cyNode = cy.$('#' + node.id);
                if (cyNode.length > 0) {
                  // @ts-ignore
                  cyNode.move({ parent: parent.id });
                }
              }, 400);
            }
          });
        }

        res.forEach((e) => {
          if (size(e.links) > 0) {
            e.links?.forEach((link) => {
              cyElementsIds.push(link.id);
            });
          }
        });

        cy.elements().unselect();
        // iterate with delay and select
        cyElementsIds.forEach((id, index) => {
          setTimeout(() => {
            cy.$(`#${id}`).select();
          }, index * 50);
        });
        DashboardComponent.tappedNode = toFocus;

        // Create paste command for undo/redo
        // Wait a bit for Cytoscape elements to be properly added
        setTimeout(() => {
          const pastedSnapshot = this.commandFactory.captureElementSnapshot(cyElementsIds.filter((id): id is string => id !== undefined));
          if (pastedSnapshot.nodes.length > 0 || pastedSnapshot.links.length > 0) {
            const pasteCommand = this.commandFactory.createPasteCommand(pastedSnapshot);
            this.undoRedoManager.pushCommand(pasteCommand);
          }
        }, 500);
      });
    }
  }

  private handleKeyEvents() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Shift') {
        ButtonPressService.isShiftPressed = true;
      }
      if (event.key === 'Alt') {
        ButtonPressService.isAltPressed = true;
        document.body.style.cursor = 'crosshair';
      }
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Shift') {
        ButtonPressService.isShiftPressed = false;
      }
      if (event.key === 'Alt') {
        ButtonPressService.isAltPressed = false;
        document.body.style.cursor = '';
      }
    });

    // If focus leaves the window while Shift/Alt is held (alt-tab, a native
    // dialog, DevTools), the matching keyup never reaches this document, so
    // the flag would otherwise stay stuck true until a page refresh.
    window.addEventListener('blur', () => {
      ButtonPressService.isShiftPressed = false;
      ButtonPressService.isAltPressed = false;
      document.body.style.cursor = '';
    });
  }

  private handleKeyDownEvent = (event: KeyboardEvent) => {
    const modifierKey = event.ctrlKey || (event.metaKey && navigator.platform.toUpperCase().indexOf('MAC') >= 0) || event.metaKey;
    const isShortcut = event.key === 'Delete' || event.key === 'Backspace'
      || ((modifierKey || event.altKey) && ['a', 'A', 'x', 'c', 'v'].includes(event.key));
    if (isShortcut && this.holdForCommentDraft(event)) return;
    if (modifierKey && (event.key === 'a' || event.key === 'A')) {
      // A focused text field keeps the native select-all.
      if (isTextFieldActive(document.activeElement)) return;
      event.preventDefault();
      if (!canvasOwnsSelectAll(document.activeElement)) return;
      if (event.shiftKey) {
        this.cyService.resetAllSelected();
      } else {
        this.cyService.selectAllElements();
      }
    } else if (event.altKey && event.key === 'a') {
      this.selectAllStream.next();
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'x') {
      if (document.activeElement !== document.body) return;
      this.cutStream.next();
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
      if (document.activeElement !== document.body) return;
      this.copyStream.next();
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      const parent = DashboardComponent.tappedNode;
      this.pasteStream.next(event);
    } else if (event.key === 'Control') {
      this.ctrlKeyPressed = true;
      document.documentElement.style.cursor = 'grab';
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      if (isTextFieldActive(document.activeElement)) return;
      this.deleteStream.next();
    }
  };

  private holdForCommentDraft(event: KeyboardEvent): boolean {
    if (!this.commentsStateManager.draftActive()) return false;
    if (!isTextFieldActive(document.activeElement)) {
      event.preventDefault();
      this.commentsStateManager.nudgeDraft();
    }
    return true;
  }

  private handleKeyUpEvent = (event: KeyboardEvent) => {
    if (event.key === 'Control') {
      this.ctrlKeyPressed = false;
      document.documentElement.style.cursor = 'auto';
    }
  };

  private trackMousePosition = (event: any) => {
    this.mousePosition = event.position;
  };

  private trackContextPressPosition = (event: any) => {
    this.contextPressPosition = event.position ? { ...event.position } : null;
  };

  public getContextPressPosition(): { x: number; y: number } | undefined {
    return this.contextPressPosition ?? undefined;
  }

  /**
   * Build the confirmation message for deletion
   */
  private buildDeletionMessage(nodeCount: number, linkCount: number, smapName: string): string {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const undoKeystroke = isMac
      ? this.translate.instant('DASHBOARD_MSG.CONFIRM.UNDO_KEYSTROKE_MAC')
      : this.translate.instant('DASHBOARD_MSG.CONFIRM.UNDO_KEYSTROKE_OTHER');

    let elementsPart = '';
    if (nodeCount > 0 && linkCount > 0) {
      const nodeText = nodeCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_PLURAL', { count: nodeCount });
      const linkText = linkCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_PLURAL', { count: linkCount });
      elementsPart = this.translate.instant('DASHBOARD_MSG.CONFIRM.ELEMENTS_COMBINED', { nodeText, linkText });
    } else if (nodeCount > 0) {
      elementsPart = nodeCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.NODE_PLURAL', { count: nodeCount });
    } else if (linkCount > 0) {
      elementsPart = linkCount === 1
        ? this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_SINGULAR')
        : this.translate.instant('DASHBOARD_MSG.CONFIRM.LINK_PLURAL', { count: linkCount });
    }

    return this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_MESSAGE', { elements: elementsPart, smapName, undoKeystroke });
  }

  private deleteSelected(cy: Core, skipConfirmation = false) {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    const currentUser = this.userService.getCurrentUserFromStorage();
    if (currentMap?.owner?.id !== currentUser?.id && !this.hasCurrentUserPermission('edit')) {
      return;
    }
    if (isTextFieldActive(document.activeElement)) return;

    const allSelected = cy.$(':selected');
    const nodes = cy.$('node:selected');
    const edges = cy.$('edge:selected');
    const topLvlNodes = getTopLevelParents(nodes as any);
    const topLvlIdsSet = new Set(topLvlNodes);

    const filteredNodes = nodes.filter((n) => topLvlIdsSet.has(n.id()));
    let nodesToDelete = !filteredNodes && nodes.length > 0 ? nodes : filteredNodes;
    if (nodesToDelete.length === 0) {
      nodesToDelete = allSelected.filter((e: any) => e.isNode());
    }

    // If nothing to delete in Cytoscape, check overlay elements
    const totalSelected = allSelected.length;
    if (totalSelected === 0) {
      // Check if an overlay element is selected and delete it
      const selectedOverlay = DashboardComponent.tappedOverlayElement;
      if (selectedOverlay?.id) {
        const element = this.overlayManager.getElements().find(e => e.id === selectedOverlay.id);
        if (element) {
          const snapshot: OverlayElementSnapshot = {element: structuredClone(element)};
          const command = new DeleteOverlayElementCommand(
            this.overlayElementService,
            this.overlayManager,
            snapshot
          );
          this.undoRedoManager.executeCommand(command);
          DashboardComponent.tappedOverlayElement = null;
          this.sidebarService.changeSelectedOverlayElement(null);
        }
      }
      return;
    }

    // Calculate total affected elements (including descendants and connected edges)
    let nodeCount = 0;
    const nodeIds = new Set<string>();
    // @ts-ignore
    for (const node of nodesToDelete) {
      if (!nodeIds.has(node.id())) {
        nodeIds.add(node.id());
        nodeCount++;
      }
      const descendants = node.descendants();
      descendants.forEach((desc: any) => {
        if (!nodeIds.has(desc.id())) {
          nodeIds.add(desc.id());
          nodeCount++;
        }
      });
    }

    const linkIds = new Set<string>();
    // @ts-ignore
    for (const edge of edges) {
      linkIds.add(edge.id());
    }
    // @ts-ignore
    for (const node of nodesToDelete) {
      const allNodes = node.union(node.descendants());
      const connectedEdges = allNodes.connectedEdges();
      connectedEdges.forEach((edge: any) => {
        linkIds.add(edge.id());
      });
    }
    const linkCount = linkIds.size;

    // Show confirmation if at least 2 elements are affected (unless skipped for cut operations)
    const totalAffected = nodeCount + linkCount;
    const shouldConfirm = !skipConfirmation && totalAffected >= 2;

    const performDeletion = async () => {
      // Collect all IDs that will be deleted (nodes, descendants, edges, and connected edges)
      const allDeletedIds = new Set<string>();

      // Add node IDs, descendant IDs, and their connected edge IDs
      // @ts-ignore
      for (const node of nodesToDelete) {
        const nodeId = node.data('id');
        allDeletedIds.add(nodeId);

        // Track all descendants recursively
        const descendants = node.descendants();
        descendants.forEach((desc: any) => {
          allDeletedIds.add(desc.data('id'));
        });

        // Track connected edges for the node and all its descendants
        const allNodes = node.union(descendants);
        const connectedEdges = allNodes.connectedEdges();
        connectedEdges.forEach((edge: any) => {
          allDeletedIds.add(edge.data('id'));
        });
      }

      // Add explicitly selected edge IDs
      // @ts-ignore
      for (const edge of edges) {
        allDeletedIds.add(edge.data('id'));
      }

      // Capture snapshot BEFORE deletion for undo
      const snapshot = this.commandFactory.captureElementSnapshot(Array.from(allDeletedIds));

      // Create delete command and execute it (which performs the actual deletion)
      const deleteCommand = this.commandFactory.createDeleteCommand(snapshot);
      await this.undoRedoManager.executeCommand(deleteCommand);

      setTimeout(() => {
        DashboardComponent.tappedNode = null;
        DashboardComponent.tappedLink = null;
        nodes.data('selected', false);
        edges.data('selected', false);
        nodes.removeClass('highlighted');
        edges.removeClass('highlighted');

        allSelected.unselect();
      }, 300);

      let attempts = 0;
      const interval = setInterval(() => {
        if (updateInProgress()) {
          if (attempts > 2) {
            updateInProgress.set(false);
            clearInterval(interval);
          }
          attempts++;
        }
      }, 1000);
    };

    if (shouldConfirm) {
      const smapName = currentMap?.name || this.translate.instant('DASHBOARD_MSG.CONFIRM.DEFAULT_SMAP_NAME');
      const message = this.buildDeletionMessage(nodeCount, linkCount, smapName);

      const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
        width: '540px',
        maxWidth: '90vw',
        data: {
          title: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_TITLE'),
          icon: 'delete',
          message: message,
          confirmText: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_CONFIRM'),
          cancelText: this.translate.instant('DASHBOARD_MSG.CONFIRM.CANCEL')
        }
      });

      dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
        if (result?.confirmed) {
          performDeletion();
        }
      });
    } else {
      performDeletion();
    }
  }

  private addAllSubs() {
    this.subs.unsubscribe();
    this.subs = new Subscription();
    const cy = this.cyService.cy;
    if (!cy) return;

    this.subs.add(
      this.getCopyStream().subscribe(() => this.copySelected(cy))
    );

    this.subs.add(
      this.getPasteStream()
        .subscribe((event) => {
        this.copyFromClipboard();
      })
    );

    this.subs.add(
      this.getDeleteStream().subscribe(() => this.deleteSelected(cy))
    );

    this.subs.add(
      this.getSelectAllStream().subscribe(() => this.cyService.selectAllElements())
    );

    this.subs.add(
      this.getCutStream().subscribe(() => this.cutSelected(cy))
    );
  }

  public copySelected(cy: Core, skipCommand = false) {
    const cyElements: any[] = [];
    const processedIds = new Set();
    const selectedNodes = cy.$('node:selected');
    const selectedIds = new Set(selectedNodes.map(n => n.id()));

    const copyNodesRecursive = (node: any) => {
      const nodeId = node.data.id;

      // Skip if already processed to avoid duplicates
      if (processedIds.has(nodeId)) {
        return;
      }
      processedIds.add(nodeId);

      // Handle collapsed children if they exist
      if (node.data.collapsed && node.data.collapsedChildren) {
        cyElements.push(...node.data.collapsedChildren.jsons());
        delete node.data.collapsedChildren;
      }

      // If node has children, copy ALL descendants
      if (node.data.children) {
        const children = cy.$(`#${nodeId}`).children();
        children.jsons().forEach((child: any) => {
          copyNodesRecursive(child);
        });
      }

      cyElements.push(node);
    };

    // First, identify top-level selected nodes (nodes that are selected but their parents are not selected)
    const topLevelSelectedNodes = selectedNodes.filter(node => {
      const parent = (node as any).parent();
      return parent.length === 0 || !selectedIds.has(parent.id());
    });

    const toCopy = cy.$(':selected');
    toCopy.filter('node').forEach((e: any) => {
      let links = e.data('links');

      if (!links || links.length === 0) {
        // Fallback to find links in Cytoscape elements
        links = [];
        cy.edges().forEach((edge) => {
          if (edge.source().id() === e.id() || edge.target().id() === e.id()) {
            const edgeData = edge.data();
            // Transform Cytoscape source/target to from/to format expected by backend
            const linkData = {
              ...edgeData,
              from: {id: edgeData.source},
              to: {id: edgeData.target}
            };
            links.push(linkData);
          }
        });
        // Save the links back to the node's data so they're included in clipboard
        if (links.length > 0) {
          e.data('links', links);
        }
      }

      if (links.length > 0) {
        links.forEach((link: GraphNodeLink, index: number) => {
          const linkElement = cy.$(`#${link.id}`);
          links[index] = assign({}, link, linkElement.data());
        });
      }
    });

    // Process only top-level selected nodes to avoid duplicates
    topLevelSelectedNodes.jsons().forEach((e: any) => {
      copyNodesRecursive(e);
    });

    const clipboardPayload = uniqBy(cyElements.filter(e => e.group === 'nodes'), 'data.id');

    // Capture clipboard state before and after
    const beforeClipboard = this.commandFactory.captureClipboardSnapshot();
    const newClipboardContent = serializeClipboardPayload(clipboardPayload);
    localStorage.setItem('clipboard', newClipboardContent);
    const afterClipboard = {content: newClipboardContent, timestamp: Date.now()};

    // Copy is not added to undo stack - it's a non-destructive operation
    // that doesn't modify the canvas, so undoing it doesn't make sense

    return {beforeClipboard, afterClipboard, elementCount: clipboardPayload.length};
  }

  public async cutSelected(cy: Core) {
    // For cut, we need to create a composite command that includes both
    // the clipboard operation and the delete operation

    const selectedNodes = cy.$('node:selected');
    const selectedEdges = cy.$('edge:selected');
    const topLvlNodes = getTopLevelParents(selectedNodes as any);
    const topLvlIdsSet = new Set(topLvlNodes);

    // Collect all IDs to delete (same logic as deleteSelected)
    const allDeletedIds = new Set<string>();
    const nodesToDelete = selectedNodes.filter((n) => topLvlIdsSet.has(n.id()));

    nodesToDelete.forEach((node: any) => {
      allDeletedIds.add(node.id());
      const descendants = node.descendants();
      descendants.forEach((desc: any) => allDeletedIds.add(desc.id()));
      const connectedEdges = node.union(descendants).connectedEdges();
      connectedEdges.forEach((edge: any) => allDeletedIds.add(edge.id()));
    });

    selectedEdges.forEach((edge: any) => {
      allDeletedIds.add(edge.id());
    });

    if (allDeletedIds.size === 0) return;

    // Capture element snapshot BEFORE any changes
    const elementsSnapshot = this.commandFactory.captureElementSnapshot(Array.from(allDeletedIds));

    // Perform copy (which updates clipboard) and get clipboard info
    const {beforeClipboard, afterClipboard, elementCount} = this.copySelected(cy, true); // skipCommand = true

    // Create the composite cut command
    const nodeCount = selectedNodes.length;
    const linkCount = selectedEdges.length;
    const cutCommand = this.commandFactory.createCutCommand(
      beforeClipboard,
      afterClipboard,
      elementsSnapshot,
      elementCount,
      nodeCount,
      linkCount
    );

    // Execute the cut command (which will perform the deletion)
    await this.undoRedoManager.executeCommand(cutCommand);

    // Clear selection state
    setTimeout(() => {
      DashboardComponent.tappedNode = null;
      DashboardComponent.tappedLink = null;
      selectedNodes.data('selected', false);
      selectedEdges.data('selected', false);
      selectedNodes.removeClass('highlighted');
      selectedEdges.removeClass('highlighted');
      cy.$(':selected').unselect();
    }, 300);
  }

  private getCopyStream() {
    return this.copyStream.asObservable();
  }

  public getDeleteStream() {
    return this.deleteStream.asObservable();
  }

  private getPasteStream() {
    return this.pasteStream.asObservable();
  }

  private getPosition() {
    return this.mousePosition;
  }

  private getSelectAllStream() {
    return this.selectAllStream.asObservable();
  }

  private getCutStream() {
    return this.cutStream.asObservable();
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.handleKeyDownEvent);
    document.removeEventListener('keyup', this.handleKeyUpEvent);
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.nodeCreationStream.next(null as unknown as GraphNode[]);
  }

  hasCurrentUserPermission(permission: Permission): boolean {
    return this.mapRoleService.hasCurrentUserPermission(permission);
  }
}

/**
 * Serialize the clipboard payload safely. `.jsons()` shallow-copies node `data`,
 * so keys like `collapsedChildren` still point at live Cytoscape Collections
 * (Collection ↔ Core ↔ elements cycle). `JSON.stringify` then throws
 * `Converting circular structure to JSON` and copy/cut silently breaks.
 *
 * Drop the cytoscape-expand-collapse plugin's live references at any depth —
 * covers nested collapsed containers too (MAP-1352, MAP-1435). If another
 * cytoscape plugin starts leaking live objects into node data, add its key here.
 */
const NON_SERIALIZABLE_CY_KEYS = new Set(['collapsedChildren']);

function serializeClipboardPayload(payload: unknown): string {
  return JSON.stringify(payload, (key, value) =>
    NON_SERIALIZABLE_CY_KEYS.has(key) ? undefined : value
  );
}
